// usePush — Web Push enablement. Detects support/permission, subscribes via the
// browser PushManager with the server's VAPID key, and registers the subscription.
// iOS Safari only allows push once the PWA is installed to the home screen.

import { useCallback, useEffect, useState } from "react";
import { PushApi } from "./useApi.js";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function usePush() {
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [status, setStatus] = useState("idle"); // idle | granted | denied | unsupported | needs-install
  const [error, setError] = useState("");

  // (Re)register the browser's push subscription with the server. Fetches the current subscription
  // (or creates one if it's missing/was invalidated) and re-registers its endpoint — idempotent
  // server-side. This is the self-heal: a subscription dropped by a service-worker update or an
  // iOS/browser rotation is silently re-established, instead of the bell showing "on" while the
  // server holds no live subscription.
  const registerSub = useCallback(async () => {
    const { enabled, publicKey } = await PushApi.key();
    if (!enabled || !publicKey) return "unconfigured";
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    const json = sub.toJSON();
    await PushApi.subscribe({ endpoint: sub.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth });
    return "granted";
  }, []);

  useEffect(() => {
    if (!supported) {
      // iOS only exposes Push once installed to the home screen.
      setStatus(isIos() && !isStandalone() ? "needs-install" : "unsupported");
      return;
    }
    if (Notification.permission === "granted") {
      setStatus("granted");
      // Self-heal a stale/missing subscription on load (best-effort — never throws to the UI).
      registerSub().catch(() => {});
    } else if (Notification.permission === "denied") setStatus("denied");
  }, [supported, registerSub]);

  // Returns an outcome code so the UI can give the user real feedback (the old version failed
  // silently — a denied permission or a config gap just looked like "the bell does nothing").
  //   granted | denied | unsupported | needs-install | unconfigured | error
  const enable = useCallback(async () => {
    setError("");
    try {
      if (!supported) {
        const s = isIos() && !isStandalone() ? "needs-install" : "unsupported";
        setStatus(s);
        return s;
      }
      // Already blocked at the browser level — requestPermission won't re-prompt, so tell the user.
      if (Notification.permission === "denied") { setStatus("denied"); return "denied"; }

      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setStatus(perm === "denied" ? "denied" : "idle"); return perm === "denied" ? "denied" : "dismissed"; }

      const outcome = await registerSub();
      if (outcome === "unconfigured") { setError("Push isn't configured on the server."); return "unconfigured"; }
      setStatus("granted");
      return "granted";
    } catch (e) {
      setError(e?.message || "Could not enable notifications.");
      return "error";
    }
  }, [supported, registerSub]);

  return { status, error, enable };
}
